'use client'
import {
  createContext,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useContext,
  useId
} from 'react'
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  useFormContext as useRHFFormContext
} from 'react-hook-form'
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  type FormLabelProps,
  type FormControlProps,
  Icon,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { MdInfoOutline } from 'react-icons/md'

// ---------------------------------------------------------------------------
// FormField context — provides id/name and required/optional flags to children
// (FormFieldLabel, FormFieldControl, FormMessage, FormDescription).
// ---------------------------------------------------------------------------

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName
  id: string
  optional?: boolean
  required?: boolean
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null)

const useFormFieldContext = (): FormFieldContextValue => {
  const ctx = useContext(FormFieldContext)
  if (!ctx) {
    throw new Error('useFormField should be used within <FormField>')
  }
  return ctx
}

// ---------------------------------------------------------------------------
// <FormField>: react-hook-form Controller wrapper that also publishes a
// context for the surrounding label/control/message components to consume.
// ---------------------------------------------------------------------------

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  optional,
  required,
  ...props
}: ControllerProps<TFieldValues, TName> & {
  optional?: boolean
  required?: boolean
}) => {
  const id = useId()

  return (
    <FormFieldContext.Provider value={{ name: props.name, id, optional, required }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// useFormField — exposes per-field state (id, error, isDirty, ...) to consumers.
// ---------------------------------------------------------------------------

export const useFormField = () => {
  const fieldContext = useFormFieldContext()
  const { getFieldState, formState } = useRHFFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)
  const { id } = fieldContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    optional: fieldContext.optional,
    required: fieldContext.required,
    ...fieldState
  }
}

// ---------------------------------------------------------------------------
// FormFieldLabel — Chakra v2 FormLabel with optional-suffix awareness.
// ---------------------------------------------------------------------------

interface FormFieldLabelProps extends FormLabelProps {
  children: ReactNode
  /** Optional help shown in a tooltip via an info icon next to the label. */
  tooltip?: ReactNode
}

export const FormFieldLabel = forwardRef<HTMLLabelElement, FormFieldLabelProps>(
  ({ children, tooltip, ...props }, ref) => {
    const { error, formItemId, optional, required } = useFormField()

    return (
      <FormLabel
        ref={ref}
        color={error ? 'status.error' : undefined}
        fontSize="sm"
        fontWeight="medium"
        htmlFor={formItemId}
        requiredIndicator={
          required ? (
            <Text as="span" color="status.error" ml={1}>
              *
            </Text>
          ) : undefined
        }
        {...props}
      >
        {children}
        {!required && optional ? (
          <Text as="span" color="text.mild" fontSize="xs" ml={1}>
            (optional)
          </Text>
        ) : null}
        {tooltip ? (
          <Tooltip label={tooltip} placement="top" fontSize="xs">
            <Icon
              as={MdInfoOutline}
              aria-label="info"
              boxSize={3.5}
              ml={1}
              color="text.mild"
              verticalAlign="middle"
              cursor="help"
            />
          </Tooltip>
        ) : null}
      </FormLabel>
    )
  }
)

FormFieldLabel.displayName = 'FormFieldLabel'

// ---------------------------------------------------------------------------
// FormFieldControl — Chakra v2 FormControl context-aware wrapper. Renders
// invalid/required/disabled state derived from the FormField context.
// ---------------------------------------------------------------------------

interface FormFieldControlProps extends Omit<FormControlProps, 'isInvalid' | 'isRequired'> {
  children: ReactNode
  disabled?: boolean
}

export const FormFieldControl = ({ children, disabled, ...props }: FormFieldControlProps) => {
  const { error, formItemId, formDescriptionId, formMessageId, required } = useFormField()

  return (
    <FormControl
      aria-describedby={!error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      id={formItemId}
      isDisabled={disabled}
      isInvalid={!!error}
      isRequired={required}
      {...props}
    >
      {children}
    </FormControl>
  )
}

// ---------------------------------------------------------------------------
// FormDescription — helper text.
// ---------------------------------------------------------------------------

interface FormDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  children: ReactNode
}

export const FormDescription = forwardRef<HTMLParagraphElement, FormDescriptionProps>(
  ({ children, ...props }, ref) => {
    const { formDescriptionId } = useFormField()

    return (
      <FormHelperText ref={ref} color="text.mild" fontSize="sm" id={formDescriptionId} {...props}>
        {children}
      </FormHelperText>
    )
  }
)

FormDescription.displayName = 'FormDescription'

// ---------------------------------------------------------------------------
// FormMessage — error text. Auto-renders the field error message; falls back
// to provided children when no error is present.
// ---------------------------------------------------------------------------

interface FormMessageProps extends HTMLAttributes<HTMLParagraphElement> {
  children?: ReactNode
}

export const FormMessage = forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ children, ...props }, ref) => {
    const { error, formMessageId } = useFormField()

    const body = error ? String(error.message ?? '') : children

    if (!body) return null

    return (
      <FormErrorMessage ref={ref} fontSize="sm" id={formMessageId} {...props}>
        {body}
      </FormErrorMessage>
    )
  }
)

FormMessage.displayName = 'FormMessage'

// Re-export RHF context hook under the GrandForm name.
export const useGrandFormContext = useRHFFormContext
